import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Layout, useGames } from './components/Layout.tsx';
import { Empty, ErrorBox } from './components/ui.tsx';
import { AcquisitionPage } from './pages/Acquisition.tsx';
import { ActivationPage } from './pages/Activation.tsx';
import { AdminPage } from './pages/Admin.tsx';
import { ComparePage } from './pages/Compare.tsx';

import { EconomicsPage } from './pages/Economics.tsx';
import { EngagementPage } from './pages/Engagement.tsx';
import { FunnelPage } from './pages/Funnel.tsx';
import { GamesPage } from './pages/Games.tsx';
import { HealthPage } from './pages/Health.tsx';
import { LivePage } from './pages/Live.tsx';
import { LoginPage } from './pages/Login.tsx';
import { LtvRoasPage } from './pages/LtvRoas.tsx';
import { MonetizationPage } from './pages/Monetization.tsx';
import { OverviewPage } from './pages/Overview.tsx';
import { RetentionPage } from './pages/Retention.tsx';
import { PlayerPage, RunDetailPage } from './pages/RunDetail.tsx';
import { RunsPage } from './pages/Runs.tsx';
import { SettingsPage } from './pages/Settings.tsx';
import { TestDetailPage, TestLabPage } from './pages/TestLab.tsx';
import { useSession } from './session.tsx';

// Creative pages (library, production, learning) load on demand: they are the heaviest part of the panel.
const CreativesPage = lazy(() => import('./pages/Creatives.tsx').then((m) => ({ default: m.CreativesPage })));
const CreativeDetailPage = lazy(() => import('./pages/Creatives.tsx').then((m) => ({ default: m.CreativeDetailPage })));
const CreativeInsightsPage = lazy(() => import('./pages/CreativeInsights.tsx').then((m) => ({ default: m.CreativeInsightsPage })));
const PlayablesPage = lazy(() => import('./pages/Playables.tsx').then((m) => ({ default: m.PlayablesPage })));
// Remote config, experiments, diagnosis, alerts, reports and the creative AI (Phase 6), on demand too.
const RemoteConfigPage = lazy(() => import('./pages/RemoteConfig.tsx').then((m) => ({ default: m.RemoteConfigPage })));
const ExperimentsPage = lazy(() => import('./pages/Experiments.tsx').then((m) => ({ default: m.ExperimentsPage })));
const ExperimentDetailPage = lazy(() => import('./pages/Experiments.tsx').then((m) => ({ default: m.ExperimentDetailPage })));
const DiagnosticsPage = lazy(() => import('./pages/Diagnostics.tsx').then((m) => ({ default: m.DiagnosticsPage })));
const AlertsPage = lazy(() => import('./pages/Alerts.tsx').then((m) => ({ default: m.AlertsPage })));
const ReportsPage = lazy(() => import('./pages/Reports.tsx').then((m) => ({ default: m.ReportsPage })));
const ReportDetailPage = lazy(() => import('./pages/Reports.tsx').then((m) => ({ default: m.ReportDetailPage })));
const CreativeAiPage = lazy(() => import('./pages/CreativeAi.tsx').then((m) => ({ default: m.CreativeAiPage })));
const CreativeAiRunPage = lazy(() => import('./pages/CreativeAi.tsx').then((m) => ({ default: m.CreativeAiRunPage })));

/** "/" opens the first active game (or the games page when there is none yet). */
function Home() {
  const games = useGames();
  if (games.error) return <ErrorBox error={games.error} onRetry={games.reload} />;
  if (!games.data) return <Empty>Cargando…</Empty>;
  const first = games.data.find((g) => !g.archived_at);
  return <Navigate to={first ? `/g/${first.id}` : '/games'} replace />;
}

export function App() {
  const { status } = useSession();
  if (status === 'checking') return <Empty>Conectando…</Empty>;
  if (status === 'anonymous') return <LoginPage />;
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="games" element={<GamesPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="g/:gameId">
          <Route index element={<OverviewPage />} />
          <Route path="diagnostics" element={<DiagnosticsPage />} />
          <Route path="engagement" element={<EngagementPage />} />
          <Route path="retention" element={<RetentionPage />} />
          <Route path="activation" element={<ActivationPage />} />
          <Route path="runs" element={<RunsPage />} />
          <Route path="runs/:runId" element={<RunDetailPage />} />
          <Route path="players/:installationId" element={<PlayerPage />} />
          <Route path="compare" element={<ComparePage />} />
          <Route path="acquisition" element={<AcquisitionPage />} />
          <Route path="creatives" element={<CreativesPage />} />
          <Route path="creatives/:creativeId" element={<CreativeDetailPage />} />
          <Route path="playables" element={<PlayablesPage />} />
          <Route path="creative-insights" element={<CreativeInsightsPage />} />
          <Route path="creative-ai" element={<CreativeAiPage />} />
          <Route path="creative-ai/:runId" element={<CreativeAiRunPage />} />
          <Route path="funnel" element={<FunnelPage />} />
          <Route path="tests" element={<TestLabPage />} />
          <Route path="tests/:testId" element={<TestDetailPage />} />
          <Route path="monetization" element={<MonetizationPage />} />
          <Route path="ltv" element={<LtvRoasPage />} />
          <Route path="economics" element={<EconomicsPage />} />
          <Route path="remote-config" element={<RemoteConfigPage />} />
          <Route path="experiments" element={<ExperimentsPage />} />
          <Route path="experiments/:key" element={<ExperimentDetailPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/:reportId" element={<ReportDetailPage />} />
          <Route path="live" element={<LivePage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="settings/:tab?" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Empty>Página no encontrada.</Empty>} />
      </Route>
    </Routes>
  );
}
